import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["activeBranchNumber"],
  _excluded2 = ["activeBranchNumber"];
import http from 'hub-http/clients/apiClient';
import { stringify } from 'hub-http/helpers/params';
import { SALES_SUBSCRIPTION, ACTIVE_ENROLLMENTS, ACTIVE_AGENT_ENROLLMENTS } from '../constants/EligibilityConstants';
import { fromJS, Map as ImmutableMap } from 'immutable';
export const fetchEnrollment = vid => {
  const url = `sequences/v2/enrollments/vid/${vid}/state`;
  return http.get(url);
};
export function fetchSequencesUsage() {
  return http.get('sequences/v2/sequences/usage');
}
export const enroll = (enrollmentSequence, email, userPlatform, origin, options) => {
  // As part of dynamic sequence changes, `activeBranchNumber` is needed for enrollment request payload
  // If the dynamic sequence is gated, `activeBranchNumber` will be `null`
  const {
      activeBranchNumber
    } = enrollmentSequence,
    sequenceData = _objectWithoutPropertiesLoose(enrollmentSequence, _excluded);
  // @ts-expect-error - we are pretending this payload is a full SequenceEnrollment but it
  // actually only contains what the backend requires
  const enrollmentPayload = activeBranchNumber !== null ? {
    activeBranchNumber,
    sequence: Object.assign({}, sequenceData, {
      userPlatform
    }),
    steps: []
  } : {
    sequence: Object.assign({}, sequenceData, {
      userPlatform
    }),
    steps: []
  };
  const data = {
    email,
    enrollment: enrollmentPayload,
    origin
  };
  const queryString = options !== null && options !== void 0 && options.queryParams ? `?${stringify(options.queryParams)}` : '';
  return http.post(`sequences/v2/enrollments/enroll${queryString}`, {
    data
  });
};
export const bulkEnroll = (enrollments, options) => {
  const data = Object.keys(enrollments).map(vid => {
    // As part of dynamic sequence changes, `activeBranchNumber` is needed for enrollment request payload
    // If the dynamic sequence is gated, `activeBranchNumber` will be `null`
    const _enrollments$vid = enrollments[vid],
      {
        activeBranchNumber
      } = _enrollments$vid,
      enrollmentData = _objectWithoutPropertiesLoose(_enrollments$vid, _excluded2);
    // @ts-expect-error - we are pretending this payload is a full SequenceEnrollment but it
    // actually only contains what the backend requires
    const enrollmentPayload = activeBranchNumber !== null ? {
      sequence: enrollmentData,
      activeBranchNumber
    } : {
      sequence: enrollmentData
    };
    return {
      enrollment: enrollmentPayload,
      vid
    };
  });
  const queryString = options !== null && options !== void 0 && options.queryParams ? `?${stringify(options.queryParams)}` : '';
  return http.post(`sequences/v2/enrollments/enroll/batch${queryString}`, {
    data
  });
};
export const pause = enrollmentId => {
  return http.post('sequences/v2/enrollments/batch/actions/pause', {
    data: {
      inputs: [enrollmentId]
    }
  });
};
export const fetchResumePreview = (enrollmentId, timeAdjustmentStrategy = 'CONTINUE_DELAY') => {
  return http.get(`sequences/v2/enrollments/${enrollmentId}/time-adjustment-preview?timeAdjustmentStrategy=${timeAdjustmentStrategy}`);
};
export const resume = (enrollment, options) => {
  const queryString = options !== null && options !== void 0 && options.queryParams ? `?${stringify(options.queryParams)}` : '';
  return http.post(`sequences/v2/enrollments/actions/update-and-unpause${queryString}`, {
    data: enrollment
  });
};
export const fetchTaskDependencies = taskUids => {
  const url = 'sequences/v2/dependencies/tasks';
  return http.post(url, {
    data: taskUids
  });
};
export function deleteBatch(sequenceIds) {
  return http.delete(`sequences/v2/sequences/batch`, {
    query: {
      overrideActiveEnrollmentCheck: true
    },
    data: {
      ids: sequenceIds
    }
  });
}
export function unenrollQuery(queryOptions) {
  const data = {
    objectTypeId: 'SEQUENCE_ENROLLMENT',
    filterGroups: queryOptions.filterGroups,
    requestOptions: {
      includeAllValues: true
    }
  };
  if (queryOptions.query) {
    data.query = queryOptions.query;
  }
  return http.post('sequences/v2/enrollments/unenroll/batch-crm-query', {
    data
  });
}
export function pauseQuery(queryOptions) {
  const data = {
    objectTypeId: 'SEQUENCE_ENROLLMENT',
    filterGroups: queryOptions.filterGroups,
    requestOptions: {
      includeAllValues: true
    }
  };
  if (queryOptions.query) {
    data.query = queryOptions.query;
  }
  return http.post('sequences/v2/enrollments/pause/batch-crm-query', {
    data
  });
}
export function resumeEnrollments(enrollmentIds, timeAdjustmentStrategy = 'CONTINUE_DELAY') {
  const queryString = `?timeAdjustmentStrategy=${timeAdjustmentStrategy}`;
  return http.post(`sequences/v2/enrollments/batch/actions/unpause${queryString}`, {
    data: {
      inputs: enrollmentIds
    }
  });
}
export function resumeQuery(queryOptions, timeAdjustmentStrategy = 'CONTINUE_DELAY') {
  const data = {
    objectTypeId: 'SEQUENCE_ENROLLMENT',
    requestOptions: {
      includeAllValues: true
    },
    filterGroups: queryOptions.filterGroups
  };
  if (queryOptions.query) {
    data.query = queryOptions.query;
  }
  const queryString = `?timeAdjustmentStrategy=${timeAdjustmentStrategy}`;
  return http.post(`sequences/v2/enrollments/unpause/batch-crm-query${queryString}`, {
    data
  });
}
export function pauseAll(userId, sequenceId = null) {
  const sequenceQuery = sequenceId ? [{
    property: 'hs_sequence_id',
    value: sequenceId,
    operator: 'EQ'
  }] : [];
  const query = {
    objectTypeId: 'SEQUENCE_ENROLLMENT',
    requestOptions: {
      includeAllValues: true
    },
    filterGroups: [{
      filters: [{
        property: 'hs_enrollment_status',
        values: ['SCHEDULED', 'IN_PROGRESS'],
        operator: 'IN'
      }, {
        property: 'hs_enrolled_by',
        value: userId,
        operator: 'EQ'
      }, ...sequenceQuery]
    }]
  };
  return http.post('sequences/v2/enrollments/pause/batch-crm-query', {
    data: query
  });
}
export function resumeAll(userId, sequenceId = null) {
  const sequenceQuery = sequenceId ? [{
    property: 'hs_sequence_id',
    value: sequenceId,
    operator: 'EQ'
  }] : [];
  const query = {
    objectTypeId: 'SEQUENCE_ENROLLMENT',
    requestOptions: {
      includeAllValues: true
    },
    filterGroups: [{
      filters: [{
        property: 'hs_enrollment_status',
        value: 'PAUSED',
        operator: 'EQ'
      }, {
        property: 'hs_enrolled_by',
        value: userId,
        operator: 'EQ'
      }, ...sequenceQuery]
    }]
  };
  return http.post('sequences/v2/enrollments/unpause/batch-crm-query/?timeAdjustmentStrategy=CONTINUE_DELAY', {
    data: query
  });
}
const ELIGIBILITY_VALIDATIONS_TO_CHECK = [SALES_SUBSCRIPTION, ACTIVE_ENROLLMENTS, ACTIVE_AGENT_ENROLLMENTS];
export function fetchEligibility(toEmail) {
  return http.post('sequences/v2/eligibility', {
    data: {
      toEmail,
      validations: ELIGIBILITY_VALIDATIONS_TO_CHECK
    }
  }).then(fromJS);
}
export function fetchEligibilityBatch(vids) {
  const vidsJS = Array.isArray(vids) ? vids : vids.toJS().filter(vid => vid !== null);
  if (!vidsJS.length) {
    return Promise.resolve(ImmutableMap());
  }
  return http.post('sequences/v2/eligibility/batch', {
    data: {
      vids: vidsJS,
      validations: ELIGIBILITY_VALIDATIONS_TO_CHECK
    }
  }).then(res => res.sequenceEnrollmentEligibilityByVid).then(fromJS);
}
export const fetchSequenceStepSummary = sequenceStepEnrollmentId => {
  return http.get(`sequences/v2/step-enrollments/${sequenceStepEnrollmentId}/extended-summary`);
};